// User roles and permissions system

export type UserRole = 'user' | 'system_admin' | 'community_admin' | 'signals_admin';

export type AdminPermission = 
  | 'user_management'
  | 'system_settings'
  | 'loyalty_management'
  | 'reports_analytics'
  | 'notifications_management'
  | 'community_management'
  | 'signals_management'
  | 'financial_management'
  | 'content_management'
  | 'product_management'
  | 'security_management';

export interface UserRoleConfig {
  role: UserRole;
  displayName: string;
  description: string;
  permissions: AdminPermission[];
  color: string;
  priority: number; // Higher number = higher priority
}

export const ROLE_CONFIGURATIONS: Record<UserRole, UserRoleConfig> = {
  user: {
    role: 'user',
    displayName: 'User',
    description: 'Regular user with standard access',
    permissions: [],
    color: '#2196f3',
    priority: 0,
  },
  system_admin: {
    role: 'system_admin',
    displayName: 'System Admin',
    description: 'Full system access with all administrative privileges',
    permissions: [
      'user_management',
      'system_settings',
      'loyalty_management',
      'reports_analytics',
      'notifications_management',
      'community_management',
      'signals_management',
      'financial_management',
      'content_management',
      'product_management',
      'security_management',
    ],
    color: '#f44336',
    priority: 100,
  },
  community_admin: {
    role: 'community_admin',
    displayName: 'Community Admin',
    description: 'Manages community features, groups, and user interactions',
    permissions: [
      'community_management',
      'content_management',
      'user_management', // Limited user management for community purposes
      'notifications_management', // Can send community notifications
    ],
    color: '#9c27b0',
    priority: 30,
  },
  signals_admin: {
    role: 'signals_admin',
    displayName: 'Signals Admin',
    description: 'Manages trading signals, analytics, and financial data',
    permissions: [
      'signals_management',
      'financial_management',
      'reports_analytics', // Can view trading reports
      'notifications_management', // Can send trading notifications
    ],
    color: '#ff9800',
    priority: 40,
  },
};

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  mobileNumber?: string;
  
  // Role system
  role: UserRole;
  isAdmin: boolean; // Deprecated but kept for backwards compatibility
  adminPermissions?: AdminPermission[]; // Custom permissions override
  
  // Profile data
  firstName?: string;
  lastName?: string;
  bio?: string;
  country?: string;
  timezone?: string;
  
  // Trading related
  tradingExperience?: string;
  preferredTradingPairs?: string;
  xmAccountNumber?: string;
  xmAffiliationVerified?: boolean;
  xmAffiliationVerifiedAt?: any;
  weltradeAccountNumber?: string;
  weltradeUserId?: string;
  derivAccountNumber?: string;
  
  // Level system
  currentXmLevel?: string;
  xmPoints?: number;
  totalCommission?: number;
  monthlyCommission?: number;
  
  // Settings
  notificationsEnabled?: boolean;
  newsletter?: boolean;
  
  // Status
  isBlocked?: boolean;
  blockedReason?: string;
  blockedAt?: any;
  blockedBy?: string;
  
  // Metadata
  createdAt: any;
  lastLogin: any;
  updatedAt?: any;
  fcmTokens?: string[];
}

// Helper functions
export const hasPermission = (userRole: UserRole, permission: AdminPermission, customPermissions?: AdminPermission[]): boolean => {
  // Check custom permissions first
  if (customPermissions && customPermissions.includes(permission)) {
    return true;
  }
  
  // Check role-based permissions
  const roleConfig = ROLE_CONFIGURATIONS[userRole];
  return roleConfig.permissions.includes(permission);
};

export const isAdminRole = (role: UserRole): boolean => {
  return role !== 'user';
};

export const getRoleDisplayName = (role: UserRole): string => {
  return ROLE_CONFIGURATIONS[role].displayName;
};

export const getRoleColor = (role: UserRole): string => {
  return ROLE_CONFIGURATIONS[role].color;
};

export const getHighestPriorityRole = (roles: UserRole[]): UserRole => {
  return roles.reduce((highest, current) => {
    return ROLE_CONFIGURATIONS[current].priority > ROLE_CONFIGURATIONS[highest].priority 
      ? current 
      : highest;
  }, roles[0] || 'user');
};

export const canManageUser = (managerRole: UserRole, targetRole: UserRole): boolean => {
  const managerPriority = ROLE_CONFIGURATIONS[managerRole].priority;
  const targetPriority = ROLE_CONFIGURATIONS[targetRole].priority;
  
  // System admins can manage everyone
  if (managerRole === 'system_admin') {
    return true;
  }
  
  // Other admins can only manage users with lower priority
  return managerPriority > targetPriority;
}; 